<template>
  <div
    v-if="shown"
    :style="{ 'min-width': showOnHeader ? 'fit-content' : undefined }"
    :class="{ 'q-mx-auto self-stretch': showOnMobileFooter }"
    data-test-id="site-selector-button"
  >
    <component
      :is="isBtnDropdown ? 'q-btn-dropdown' : 'q-btn'"
      v-if="showOnHeader"
      flat
      dense
      rounded
      outline
      :size="buttonSize"
      :ripple="false"
      :class="siteSelectorButtonClass"
      style="min-width: fit-content;"
      @click="showSiteSelectorPopUp()"
    >
      <template
        v-if="isBtnDropdown"
        #label
      >
        <div class="row items-center no-wrap ">
          <q-icon
            class="q-px-none"
            :left="isBtnDropdown"
            name="fal fa-location-dot"
          />
          <div
            v-if="isBtnDropdown"
            class="text-left ellipsis"
            style="max-width: 30vw"
          >
            {{ displayName }}
          </div>
        </div>
      </template>
      <div
        v-if="!isBtnDropdown"
        class="row items-center no-wrap "
      >
        <q-icon
          class="q-px-none q-mx-none"
          left
          name="fal fa-location-dot"
        />
        <div
          v-if="!hasOtherIcons"
          class="text-left ellipsis q-px-none"
          style="height: fit-content"
          :style="{ 'max-width': isFullWidth ? '100vw' : '30vw' }"
        >
          {{ displayName }}
        </div>
      </div>
      <q-item
        v-if="isBtnDropdown"
        v-close-popup
        style="width: 40vw; max-width: 340px;"
      >
        <q-item-section class="q-py-sm">
          <q-item-label class="column full-width">
            <q-img
              v-if="siteThumbnailImage"
              :src="currentSite.siteThumbnailImageUrl"
              :ratio="3/2"
              style="width: 100%;  display: block"
              class="q-mb-sm col"
            />
          </q-item-label>
          <site-selector-pop-up
            :use-dark-mode="$q.dark.isActive || darkMode"
            :show-change-location-button="showChangeLocationButton"
            @show-site-selector-drawer="$emit('show-site-selector-drawer')"
          />
        </q-item-section>
      </q-item>
    </component>
    <template v-else-if="showAsSelectButton">
      <q-select
        v-model="selectedSiteId"
        :options="siteSelectorOptions"
        option-label="name"
        option-value="siteId"
        filled
        bg-color="transparent"
        dense
        class="text-body1"
        style="border-bottom: 1px solid; display: inline-block;"
        :style="{ 'border-color': darkMode || $q.dark.isActive ? 'white' : 'grey' }"
        :dark="$q.dark.isActive || darkMode"
        emit-value
        map-options
        clearable
        use-input
        @filter="filterSiteOptions"
        @update:model-value="changeSites()"
      >
        <template #no-option>
          <q-item>
            <q-item-section class="text-grey">
              {{ $t('search.noResults') }}
            </q-item-section>
          </q-item>
        </template>
      </q-select>
    </template>

    <site-selector
      v-if="shown && showSiteSelector"
      :is-widget="false"
      :show-image="!isMoviePage && $q.screen.gt.xs"
      :image-url="currentSite.siteThumbnailImageUrl"
      :site-selectors-display-type="currentSite.siteThumbnailImageUrl ? SITE_SELECTOR_DISPLAY_TYPES.BLOCK : SITE_SELECTOR_DISPLAY_TYPES.BUTTON"
      :title="$t('siteLocation.selectLocationHeading') || $t('siteLocation.selectLocationPrompt')"
      :show-title="isMoviePage"
      :site-selector-items="siteSelectorItems"
      :show-subtitle="$q.screen.lt.sm || isPhonePreview"
      :subtitle="currentSite.addressFull"
      :site-detail-link-url="currentLocationDetailLinkUrl"
      :allow-dismiss="true"
      :delay-in-seconds="0"
      :button-type="buttonType"
      :button-color="buttonColor"
      :button-color-hex="buttonColorHex"
      :border-radius="borderRadius"
      :block-background-color="blockBackgroundColor"
      :block-background-color-hex="blockBackgroundColorHex"
      :block-text-color="blockTextColor"
      :block-text-color-hex="blockTextColorHex"
      widget-display-type="pop-up"
      :is-phone-preview="isPhonePreview"
      :show-change-location-button="showChangeLocationButton"
      :is-image-full-width="isImageFullWidth"
      :is-movie-page="isMoviePage"
      :show-site-selector-pop-up="true"
      :use-dark-mode="$q.dark.isActive || darkMode"
      :is-mobile-view="isMobileView"
      @dismiss-site-selector="dismissSiteSelector()"
      @show-site-selector-drawer="$emit('show-site-selector-drawer')"
    />
  </div>
</template>

<script>
import { BUTTON_TYPES, SITE_SELECTOR_DISPLAY_TYPES, WIDGET_DISPLAY_TYPES } from 'src/models/page/Section'
import SiteSelector from 'src/modules/consumer/components/site-selector/SiteSelector'
import SiteSelectorPopUp from 'src/modules/consumer/components/site-selector/SiteSelectorPopUp'
import UseCurrentLocationButton from 'src/modules/consumer/components/site-selector/UseCurrentLocationButton'

export default {
  name: 'SiteSelectorButton',
  components: { SiteSelector, SiteSelectorPopUp, UseCurrentLocationButton },
  props: {
    shown: {
      type: Boolean,
      required: false,
      default: false
    },
    showOnHeader: {
      type: Boolean,
      required: false,
      default: false
    },
    showOnMobileFooter: {
      type: Boolean,
      required: false,
      default: false
    },
    showAsSelectButton: {
      type: Boolean,
      required: false,
      default: false
    },
    siteThumbnailImage: {
      type: String,
      required: false,
      default: ''
    },
    currentLocationDetailLinkUrl: {
      type: String,
      required: false,
      default: null
    },
    hideCurrentLocation: {
      type: Boolean,
      required: false,
      default: false
    },
    siteSelectorSiteIds: {
      type: Array,
      required: false,
      default: () => ([])
    },
    siteSelectorDisplayType: {
      type: String,
      required: false,
      default: SITE_SELECTOR_DISPLAY_TYPES.BUTTON
    },
    buttonType: {
      type: String,
      required: false,
      default: BUTTON_TYPES.OUTLINE
    },
    buttonColor: {
      type: String,
      required: false,
      default: 'primary'
    },
    buttonColorHex: {
      type: String,
      required: false,
      default: '#FFFFFF'
    },
    borderRadius: {
      type: Number,
      required: false,
      default: 5
    },
    blockBackgroundColor: {
      type: String,
      required: false,
      default: 'primary'
    },
    blockBackgroundColorHex: {
      type: String,
      required: false,
      default: '#FFFFFF'
    },
    blockTextColor: {
      type: String,
      required: false,
      default: 'custom'
    },
    blockTextColorHex: {
      type: String,
      required: false,
      default: '#FFFFFF'
    },
    siteSelectorIncludeAddressDetail: {
      type: Boolean,
      required: false,
      default: false
    },
    showLocationsFilter: {
      type: Boolean,
      required: false,
      default: false
    },
    isPhonePreview: {
      type: Boolean,
      default: false
    },
    darkMode: {
      type: Boolean,
      default: false
    },
    showChangeLocationButton: {
      type: Boolean,
      default: false
    },
    isImageFullWidth: {
      type: Boolean,
      default: false
    },
    hasOtherIcons: {
      type: Boolean,
      default: false
    },
    isMoviePage: {
      type: Boolean,
      defaut: false
    },
    isMobileView: {
      type: Boolean,
      defaut: false
    },
    siteSelectorButtonClass: {
      type: String,
      default: ''
    },
    isFullWidth: {
      type: Boolean,
      default: false
    },
    buttonSize: {
      type: String,
      default: 'md'
    }
  },
  emits: ['show-site-selector-drawer', 'site-selector-shown'],
  data () {
    return {
      showSiteSelector: false,
      selectedSiteId: this.currentSite.name,
      sitesFilterString: ''
    }
  },

  configuration: ['currentSite', 'sites'],
  computed: {
    isCinemaGroupSite () {
      return this.currentSite.consumerConfig.showMoviesAndShowingsForMultipleSites || (this.currentSite.consumerConfig.showSiteSelectorOnMoviePage && this.currentSite.consumerConfig.hideCurrentLocation)
    },
    isBtnDropdown () {
      return this.$q.screen.gt.xs && !this.isCinemaGroupSite
    },
    displayName () {
      if (this.isCinemaGroupSite) {
        return this.$t('siteLocation.selectLocationHeading')
      } else {
        return this.currentSite.displayName
      }
    },
    siteSelectorItems () {
      return _(this.sites.data).map(site => {
        if (_.includes(this.siteSelectorSiteIds, site.id)) {
          return {
            name: site.name,
            address: this.siteSelectorIncludeAddressDetail ? site.addressShort : null,
            siteId: site.id,
            image: site.consumerConfig.siteThumbnailImage
          }
        } else {
          return null
        }
      }).compact().value()
    },
    siteSelectorOptions () {
      if (!this.sitesFilterString) {
        return this.siteSelectorItems
      }

      const keywords = this.sitesFilterString.toLowerCase().split(' ')

      return _.filter(this.siteSelectorItems, site => {
        const modifierDisplayName = site.name.toLowerCase()
        return _.some(keywords, keyword => modifierDisplayName.includes(keyword))
      })
    }
  },
  created () {
    _.extend(this, { BUTTON_TYPES, WIDGET_DISPLAY_TYPES, SITE_SELECTOR_DISPLAY_TYPES })
  },
  methods: {
    redirect (url) {
      window.location = url
    },
    handleClick () {
      if (this.currentLocationDetailLinkUrl) {
        window.location = this.currentLocationDetailLinkUrl
      }
    },
    showSiteSelectorPopUp () {
      if (this.isCinemaGroupSite) {
        this.$emit('show-site-selector-drawer')
      } else {
        if (this.$q.screen.lt.sm) this.showSiteSelector = true

        this.$emit('site-selector-shown', true)
      }
      this.$trackEvent(this.$customerEvents.CLICKED_SITE_SELECTOR_ON_NAVIGATION)
    },
    changeSites () {
      const newSite = _.find(this.sites.data, site => site.id === this.selectedSiteId)

      if (!newSite || newSite.id === this.currentSite.id) return

      const url = newSite.url(window.location.pathname)
      this.redirect(this.$appendCurrentQueryParams(url))
    },
    dismissSiteSelector () {
      this.showSiteSelector = false
      this.$emit('site-selector-shown', false)
    },
    filterSiteOptions (value, update) {
      update(() => {
        this.sitesFilterString = value.length ? value.trim() : null
      })
    }
  }
}
</script>

<style lang="scss" scoped>

.q-btn .on-left{
  margin-right: 2px;
}
.bordered {
  border: 1px solid;
}

</style>
